/**
 * `localStorage` that never throws: private windows, blocked site data, quota errors and SSR all
 * degrade gracefully. In the browser a failed write falls back to an in-memory map so a choice still
 * lasts for the session; on the server nothing is stored (and nothing can leak between requests).
 */

const memory = new Map<string, string>();

function getStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null; // SecurityError when site data is blocked
  }
}

export function readStorage(key: string): string | null {
  if (typeof window === 'undefined') return null;
  const storage = getStorage();
  if (storage) {
    try {
      const value = storage.getItem(key);
      if (value !== null) return value;
    } catch {
      /* fall through to memory */
    }
  }
  return memory.get(key) ?? null;
}

export function writeStorage(key: string, value: string): void {
  if (typeof window === 'undefined') return;
  const storage = getStorage();
  if (storage) {
    try {
      storage.setItem(key, value);
      memory.delete(key);
      return;
    } catch {
      /* quota exceeded / blocked → memory fallback below */
    }
  }
  memory.set(key, value);
}

/** Test helper: forget values that only ever lived in the in-memory fallback. */
export function clearStorageMemory(): void {
  memory.clear();
}

export function removeStorage(key: string): void {
  if (typeof window === 'undefined') return;
  memory.delete(key);
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/** Parse a stored JSON value; returns `undefined` for missing or corrupt data. */
export function readJson(key: string): unknown {
  const raw = readStorage(key);
  if (raw === null) return undefined;
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return undefined;
  }
}

export function writeJson(key: string, value: unknown): void {
  try {
    writeStorage(key, JSON.stringify(value));
  } catch {
    /* unserialisable value: ignore */
  }
}
